import React, { useEffect, useState } from 'react';
import { Card, Badge, Button, Spinner, DeleteConfirmDialog } from '@/components/ui';
import { CreditCard, Users, Coins, ArrowUpRight, Download, ExternalLink, Loader2, Plus, Receipt, PackagePlus, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { PLAN_CONFIG } from './OrgSettingsPanel';
import { toDate } from '@/lib/date-utils';
import { useTokenBalance } from '@/hooks/useTokenBalance';
import { useUserTimezone } from '@/hooks/useDateFormat';
import { apiRequest } from '@/services/api';
import { useToast } from '@/contexts/ToastContext';
import { UpgradePlanModal } from '@/components/billing/UpgradePlanModal';
import { BuyTokensModal } from '@/components/billing/BuyTokensModal';
import {
  getInvoices,
  getBillingAddons,
  removeRecurringAddon,
  downloadInvoicePdf,
  type Invoice,
  type BillingAddon,
} from '@/services/billingService';

export function BillingSettingsPanel() {
  const { activeOrganization, userRole } = useAuth();
  const { balance, purchasedTokens, grant, used, maxUsers, unlimited, percentUsed, loading: quotaLoading } = useTokenBalance();
  const { success, error: toastError } = useToast();
  const tz = useUserTimezone();
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [buyTokensOpen, setBuyTokensOpen] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [invoicesLoading, setInvoicesLoading] = useState(true);
  const [addonCatalog, setAddonCatalog] = useState<BillingAddon[]>([]);
  const [removingItem, setRemovingItem] = useState<string | null>(null);
  // Add-on waiting for confirmation in the DeleteConfirmDialog before it is removed
  const [pendingRemove, setPendingRemove] = useState<{ stripeItemId: string; addonId: string } | null>(null);
  const [downloadingInvoiceId, setDownloadingInvoiceId] = useState<string | null>(null);

  const orgId = activeOrganization?.id;

  const handleDownloadInvoice = async (invoice: Invoice) => {
    if (!orgId) return;
    setDownloadingInvoiceId(invoice.id);
    try {
      await downloadInvoicePdf(orgId, invoice);
    } catch (err: any) {
      console.error('[BillingSettingsPanel] invoice PDF download failed:', err);
      toastError(err?.message || 'Failed to download the invoice PDF');
    } finally {
      setDownloadingInvoiceId(null);
    }
  };

  // Local copy of the active recurring add-ons so a Remove reflects immediately
  // (the org subscription won't re-fetch until the next auth refresh).
  const [recurringAddons, setRecurringAddons] = useState(
    activeOrganization?.subscription?.recurringAddons ?? [],
  );
  useEffect(() => {
    setRecurringAddons(activeOrganization?.subscription?.recurringAddons ?? []);
  }, [activeOrganization?.subscription?.recurringAddons]);

  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    (async () => {
      setInvoicesLoading(true);
      try {
        const fetched = await getInvoices(orgId);
        if (!cancelled) setInvoices(fetched);
      } catch (err: any) {
        console.error('[BillingSettingsPanel] failed to load invoices:', err);
        if (!cancelled) setInvoices([]);
      } finally {
        if (!cancelled) setInvoicesLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  // Public add-on catalog — used to resolve recurring-add-on display names.
  useEffect(() => {
    if (!orgId || recurringAddons.length === 0) return;
    let cancelled = false;
    (async () => {
      try {
        const fetched = await getBillingAddons(orgId);
        if (!cancelled) setAddonCatalog(fetched);
      } catch (err: any) {
        console.error('[BillingSettingsPanel] failed to load add-on catalog:', err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, recurringAddons.length]);

  if (!activeOrganization) return null;

  const { subscription } = activeOrganization;
  const plan = subscription?.plan;
  const hasStripe = !!subscription?.stripeSubscriptionId;
  const canUpgrade = userRole === 'owner' || userRole === 'admin';

  const addonName = (addonId: string): string =>
    addonCatalog.find((a) => a.id === addonId)?.displayName ?? addonId;

  async function handleRemoveRecurring(stripeItemId: string) {
    if (!orgId) return;
    setRemovingItem(stripeItemId);
    try {
      await removeRecurringAddon(orgId, stripeItemId);
      setRecurringAddons((prev) => prev.filter((a) => a.stripeItemId !== stripeItemId));
      success('Add-on removed from your subscription');
    } catch (err: any) {
      console.error('[BillingSettingsPanel] remove-recurring error:', err);
      toastError(err?.message ?? 'Failed to remove add-on. Please try again.');
    } finally {
      setRemovingItem(null);
    }
  }

  const statusConfig: Record<string, { label: string; variant: 'success' | 'default' | 'destructive' | 'warning' }> = {
    active:    { label: 'Active',    variant: 'success' },
    trial:     { label: 'Trial',     variant: 'default' },
    expired:   { label: 'Expired',   variant: 'destructive' },
    past_due:  { label: 'Past due',  variant: 'warning' },
    canceled:  { label: 'Canceled',  variant: 'destructive' },
  };

  const formatBillingPeriod = (start: any, end: any): string => {
    const s = start ? toDate(start) : null;
    const e = end   ? toDate(end)   : null;
    const fmt = (d: Date) =>
      d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: tz });
    if (s && e) return `${fmt(s)} – ${fmt(e)}`;
    if (s)      return `Started ${fmt(s)}`;
    if (e)      return `Ends ${fmt(e)}`;
    return '';
  };

  const status = subscription?.status;
  const statusCfg = status ? statusConfig[status] : undefined;

  // Fill-level scale: green < 50%, orange 50–75%, red ≥ 75% (matches the top-bar quota ring).
  const progressColor =
    percentUsed == null ? 'bg-accent-blue' :
    percentUsed >= 75   ? 'bg-status-error' :
    percentUsed >= 50   ? 'bg-status-warning' :
                          'bg-status-success';

  const billingPeriodLabel =
    formatBillingPeriod(subscription?.billingPeriodStart, subscription?.billingPeriodEnd);

  const invoiceStatusVariant: Record<Invoice['status'], 'success' | 'warning' | 'secondary'> = {
    paid: 'success',
    open: 'warning',
    void: 'secondary',
  };

  const formatCents = (cents: number, currency: string): string => {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: (currency || 'USD').toUpperCase(),
      }).format(cents / 100);
    } catch {
      return `$${(cents / 100).toLocaleString()}`;
    }
  };

  const formatInvoiceDate = (value: any): string => {
    const d = toDate(value);
    return d ? d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric', timeZone: tz }) : '—';
  };

  async function openPortal() {
    if (!activeOrganization?.id) return;
    setPortalLoading(true);
    try {
      const { url } = await apiRequest<{ url: string }>(
        `/billing/portal?returnUrl=${encodeURIComponent(window.location.href)}`,
        { method: 'GET' },
        activeOrganization.id,
      );
      window.location.href = url;
    } catch (err: any) {
      toastError(err?.message ?? 'Failed to open billing portal. Please try again.');
      setPortalLoading(false);
    }
  }

  return (
    <div className="space-y-4">

      {/* Plan card */}
      <Card padding="lg">
        <div className="flex items-start justify-between gap-4">

          {/* Left: icon + plan identity */}
          <div className="flex items-start gap-3 min-w-0">
            <div className="mt-0.5 w-9 h-9 rounded-lg bg-muted flex items-center justify-center shrink-0">
              <CreditCard className="w-4 h-4 text-muted-foreground" />
            </div>
            <div className="min-w-0">
              <h3 className="text-h2 font-semibold text-foreground leading-snug">
                {plan && PLAN_CONFIG[plan] ? PLAN_CONFIG[plan].label : 'No Plan'}
              </h3>
              <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                {plan && PLAN_CONFIG[plan] && (
                  <Badge className={PLAN_CONFIG[plan].className}>
                    {PLAN_CONFIG[plan].label}
                  </Badge>
                )}
                {statusCfg && (
                  <Badge variant={statusCfg.variant}>{statusCfg.label}</Badge>
                )}
              </div>
              {billingPeriodLabel && (
                <p className="text-sm text-muted-foreground mt-2">
                  {billingPeriodLabel}
                </p>
              )}
            </div>
          </div>

          {/* Right: CTAs */}
          {canUpgrade && (
            <div className="flex flex-col gap-2 shrink-0">
              {plan !== 'enterprise' && (
                <Button
                  variant="default"
                  size="sm"
                  onClick={() => setUpgradeOpen(true)}
                >
                  <ArrowUpRight className="w-4 h-4 mr-1.5" />
                  Upgrade
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setBuyTokensOpen(true)}
              >
                <Plus className="w-3.5 h-3.5 mr-1.5" />
                Buy more tokens
              </Button>
              {hasStripe && (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={portalLoading}
                  onClick={openPortal}
                >
                  {portalLoading ? (
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                  ) : (
                    <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                  )}
                  Manage billing
                </Button>
              )}
            </div>
          )}
        </div>
      </Card>

      {/* Usage & Limits card */}
      <Card padding="lg">
        <h3 className="text-h3 font-semibold text-foreground mb-4">Usage &amp; Limits</h3>
        <div className="grid grid-cols-2 gap-3">

          {/* Tokens tile — balance remaining of the monthly allowance */}
          <div className="rounded-lg border border-border bg-muted/40 p-4 flex flex-col gap-2">
            <div className="flex items-center gap-1.5">
              <Coins className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Tokens
              </span>
            </div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-2xl font-semibold text-foreground leading-none">
                {quotaLoading
                  ? '—'
                  : unlimited
                    ? '∞'
                    : balance != null
                      ? balance.toLocaleString()
                      : '—'}
              </span>
              {!unlimited && grant != null && !quotaLoading && (
                <span className="text-sm text-muted-foreground">
                  / {grant.toLocaleString()}
                </span>
              )}
            </div>
            {!quotaLoading && purchasedTokens != null && purchasedTokens > 0 && (
              <p className="text-xs text-status-success">
                + {purchasedTokens.toLocaleString()} purchased (never expire)
              </p>
            )}
            {!unlimited && (
              <div className="w-full h-2 bg-muted rounded-full overflow-hidden">
                <div
                  className={`h-full rounded-full transition-all duration-300 ${progressColor}`}
                  style={{ width: `${percentUsed ?? 0}%` }}
                />
              </div>
            )}
            <p className="text-xs text-muted-foreground">
              {quotaLoading
                ? '—'
                : unlimited
                  ? 'No limit on your plan'
                  : used != null
                    ? `${used.toLocaleString()} used · 1 token = 1 test (up to 5 min)`
                    : '1 token = 1 test (up to 5 min)'}
            </p>
            {!quotaLoading && !unlimited && (
              <p className="text-xs leading-relaxed text-muted-foreground">
                Each test uses at least 1 token. A token covers 5 minutes of run
                time — a longer test uses one more token per additional 5 minutes.
              </p>
            )}
          </div>

          {/* Max Users tile */}
          <div className="rounded-lg border border-border bg-muted/40 p-4 flex flex-col gap-2">
            <div className="flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5 text-muted-foreground" />
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Max Users
              </span>
            </div>
            <div className="text-2xl font-semibold text-foreground leading-none">
              {quotaLoading ? '—' : maxUsers != null ? maxUsers.toLocaleString() : '—'}
            </div>
            <div className="flex-1" />
            <p className="text-xs text-muted-foreground">Team seat limit</p>
          </div>

        </div>
      </Card>

      {/* Active add-ons card */}
      {recurringAddons.length > 0 && (
        <Card padding="lg">
          <div className="flex items-center gap-2 mb-4">
            <PackagePlus className="w-4 h-4 text-muted-foreground" />
            <h3 className="text-h3 font-semibold text-foreground">Active add-ons</h3>
          </div>
          <div className="divide-y divide-border">
            {recurringAddons.map((addon) => {
              const isRemoving = removingItem === addon.stripeItemId;
              return (
                <div
                  key={addon.stripeItemId}
                  className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">
                      {addonName(addon.addonId)}
                    </p>
                    <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                      {addon.quantity != null && addon.quantity > 1 && (
                        <Badge variant="secondary">× {addon.quantity}</Badge>
                      )}
                      {typeof addon.tokens === 'number' && (
                        <Badge variant="secondary">
                          {addon.tokens.toLocaleString()} tokens
                        </Badge>
                      )}
                    </div>
                  </div>
                  {canUpgrade && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={isRemoving}
                      onClick={() => setPendingRemove({ stripeItemId: addon.stripeItemId, addonId: addon.addonId })}
                    >
                      {isRemoving ? (
                        <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                      ) : (
                        <X className="w-3.5 h-3.5 mr-1.5" />
                      )}
                      Remove
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* Billing history card */}
      <Card padding="lg">
        <div className="flex items-center gap-2 mb-4">
          <Receipt className="w-4 h-4 text-muted-foreground" />
          <h3 className="text-h3 font-semibold text-foreground">Billing history</h3>
        </div>

        {invoicesLoading ? (
          <div className="flex items-center justify-center py-10">
            <Spinner />
          </div>
        ) : invoices.length === 0 ? (
          <p className="text-sm text-muted-foreground py-2">No invoices yet.</p>
        ) : (
          <div className="divide-y divide-border">
            {invoices.map((invoice) => {
              const statusVariant = invoiceStatusVariant[invoice.status] ?? 'secondary';
              return (
                <div key={invoice.id} className="py-3 first:pt-0 last:pb-0">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-medium text-foreground">
                          {formatInvoiceDate(invoice.createdAt)}
                        </span>
                        <Badge variant={statusVariant}>{invoice.status}</Badge>
                      </div>
                      {invoice.reference && (
                        <p className="text-xs text-muted-foreground mt-0.5 truncate">
                          {invoice.reference}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="text-sm font-semibold text-foreground">
                        {formatCents(invoice.amountCents, invoice.currency)}
                      </span>
                      {invoice.billingMethod === 'wire' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          title="Download invoice PDF"
                          disabled={downloadingInvoiceId === invoice.id}
                          onClick={() => handleDownloadInvoice(invoice)}
                        >
                          {downloadingInvoiceId === invoice.id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Download className="w-3.5 h-3.5" />
                          )}
                        </Button>
                      )}
                    </div>
                  </div>

                  {invoice.lineItems && invoice.lineItems.length > 0 && (
                    <ul className="mt-2 space-y-1 pl-1">
                      {invoice.lineItems.map((line, idx) => (
                        <li
                          key={idx}
                          className="flex items-center justify-between gap-2 text-xs text-muted-foreground"
                        >
                          <span className="truncate">
                            {line.description}
                            {line.quantity > 1 ? ` × ${line.quantity}` : ''}
                          </span>
                          <span className="shrink-0">
                            {formatCents(line.amountCents, invoice.currency)}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </Card>

      <UpgradePlanModal
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        currentPlan={plan}
      />

      <BuyTokensModal
        open={buyTokensOpen}
        onClose={() => setBuyTokensOpen(false)}
      />

      <DeleteConfirmDialog
        open={pendingRemove !== null}
        onClose={() => setPendingRemove(null)}
        title="Remove add-on?"
        verb="remove"
        confirmText="Remove"
        itemName={pendingRemove ? addonName(pendingRemove.addonId) : undefined}
        details="It is removed from your subscription."
        onConfirm={async () => {
          if (pendingRemove) await handleRemoveRecurring(pendingRemove.stripeItemId);
        }}
      />
    </div>
  );
}
